import { useCallback } from 'react';

import {
  type CommonListInsertAt,
  commonCreate,
} from '../../../redux/common_list/thunks';
import type { StoreEntityKey } from '../../../redux/entities/types';
import type { EndpointResponseNormalized } from '../../../redux/middlewares/http_normalizer/types';
import { useAppDispatch } from '../../../redux/redux_hooks';
import type { AppDispatch } from '../../../redux/types';
import { useListState } from './use_list_state';

type Config = {
  insertAt?: CommonListInsertAt;
};

type UseEntityListCreateOptions<
  EntityType extends StoreEntityKey,
  ListName extends string,
  CreateResult extends EndpointResponseNormalized,
  CreateParams,
> = {
  entityType: EntityType;
  listName: ListName;
  createItem?: (
    dispatch: AppDispatch,
    params: CreateParams,
  ) => Promise<CreateResult>;
  config?: Config;
};

export function useEntityListCreate<
  EntityType extends StoreEntityKey,
  ListName extends string,
  CreateResult extends EndpointResponseNormalized,
  CreateParams,
>({
  config,
  createItem,
  listName,
}: UseEntityListCreateOptions<
  EntityType,
  ListName,
  CreateResult,
  CreateParams
>) {
  const dispatch = useAppDispatch();
  useListState(listName);

  const createEntity = useCallback(
    async (params: CreateParams) => {
      if (!createItem) {
        throw new Error('Cannot create entity: createItem not defined');
      }
      return commonCreate({
        listName,
        insertAt: config?.insertAt,
        createPromise: createItem(dispatch, params),
        dispatch,
      });
    },
    [createItem, listName, config?.insertAt],
  );

  return {
    createEntity,
  };
}
